import { ref } from 'vue';
import type { Ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import type { AutoCompleteCompleteEvent } from 'primevue/autocomplete';

const SCROLL_THRESHOLD = 100;
const DEFAULT_DEBOUNCE_MS = 300;

export interface PaginatedResult<T> {
  data?: T[] | null;
  total_pages?: number | null;
}

export interface UseAutocompletePaginationOptions<T> {
  fetchFn: (query: string, page: number) => Promise<PaginatedResult<T> | null | undefined>;
  debounceMs?: number;
}

export interface UseAutocompletePaginationReturn<T> {
  suggestions: Ref<T[]>;
  isLoading: Ref<boolean>;
  hasMore: Ref<boolean>;
  onComplete: (event: AutoCompleteCompleteEvent) => void;
  passThrough: { listContainer: { onScroll: (event: Event) => void } };
  reset: () => void;
}

export function useAutocompletePagination<T>(
  options: UseAutocompletePaginationOptions<T>,
): UseAutocompletePaginationReturn<T> {
  const { fetchFn, debounceMs = DEFAULT_DEBOUNCE_MS } = options;

  const suggestions = ref<T[]>([]) as Ref<T[]>;
  const isLoading = ref(false);
  const hasMore = ref(false);
  const currentQuery = ref('');
  const currentPage = ref(1);

  async function fetchPage(query: string, page: number, append = false): Promise<void> {
    isLoading.value = true;
    try {
      const response = await fetchFn(query, page);
      const data = response?.data ?? [];
      suggestions.value = append ? [...suggestions.value, ...data] : data;
      hasMore.value = page < (response?.total_pages ?? 1);
      currentPage.value = page;
    } catch {
      if (!append) {
        suggestions.value = [];
        hasMore.value = false;
      }
    } finally {
      isLoading.value = false;
    }
  }

  const onComplete = useDebounceFn(async (event: AutoCompleteCompleteEvent) => {
    currentQuery.value = event.query;
    await fetchPage(event.query, 1);
  }, debounceMs);

  function handleListScroll(event: Event): void {
    if (isLoading.value || !hasMore.value) return;
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD) {
      fetchPage(currentQuery.value, currentPage.value + 1, true);
    }
  }

  const passThrough = { listContainer: { onScroll: handleListScroll } };

  function reset(): void {
    suggestions.value = [];
    isLoading.value = false;
    hasMore.value = false;
    currentQuery.value = '';
    currentPage.value = 1;
  }

  return { suggestions, isLoading, hasMore, onComplete, passThrough, reset };
}
